import { useCallback, useEffect, useMemo, useState } from "react";

import { useSelector } from "react-redux";
import { useImportContext } from "../importContext";
import { batchCreateQueryLayersWithTables } from "../../../../resources/layers";
import { getImportTaskParams } from "./useImportPolling";
import { cancelTask, pipelineTask } from "../../../../resources/scheduler";
import { useProgress } from "../../../../hooks/scheduler/useProgress";
import { collectErrorLog } from "../../../../utils/collectErrorLog";
import { useDialogReset } from "../../../../hooks/useDialogs";
import { createLayerInput, LayerInputConfiguration } from "../../../../evergis/core/layer/createLayerInput";
import { getLocation } from "../../../../ducks/location/selectors";
import { useAddLayersToCurrentProject } from "../../../../hooks/project/useAddLayersToCurrentProject";
import { createProject } from "../../../../resources/projects";
import { useDefaultProjectContent } from "../../../../hooks/project/useDefaultProjectContent";
import { getResourceName } from "../../../../utils/resourceName";
import { createLayerItems } from "../../../../evergis/core/project/createProjectContent";
import { getIdAttribute } from "../../../../utils/getIdAttribute";
import { createGidColumn } from "../../../../utils/createGidColumn";
import { useDefaultScaleRange } from "../../../../hooks/style/useDefaultScaleRange";
import { SchemaParsed } from "../../../../evergis/core/import/parseSchema";
import { SchedulerTask } from "../../../../interfaces/scheduler";
import { ImportAttribute } from "../types";
import { RoutePanelName } from "../../../../appConfig/types";

interface UseGDBImportApply {
  layersCreating: boolean;
  copyingLayers: boolean;
  processed: number;
  isImportDone?: boolean;
  error: { message: string; count: number; log: string };
  onApply: VoidFuncAsync;
  onCancel: VoidFuncAsync;
}

// eslint-disable-next-line max-lines-per-function
export const useGDBImportJob = (): UseGDBImportApply => {
  const { meta, file, fileType, dataSchema, objects, srFields } = useImportContext();
  const [createLayers, { data: createdLayersData, loading: creatingLayersWithTables }] =
    batchCreateQueryLayersWithTables.use();
  const [runCopyTask, { data: taskResult }] = pipelineTask.use();
  const { processed, isTaskCompleted, result } = useProgress(taskResult && taskResult.taskId);
  const [importInProgress, setInProgress] = useState(false);
  const [error, setError] = useState<string>();
  const [errorsCount, setErrorsCount] = useState<number>(0);
  const [errorLog, setErrorLog] = useState<string>();
  const [createNewProject] = createProject.use();
  const [cancel] = cancelTask.use();
  const closeDialog = useDialogReset();
  const location = useSelector(getLocation);
  const addLayersToCurrentProject = useAddLayersToCurrentProject();
  const defaultProjectContent = useDefaultProjectContent();
  const defaultScaleRange = useDefaultScaleRange();

  const processedLayers: SchemaParsed[] = useMemo(() => {
    return (dataSchema as SchemaParsed[]).filter(schemaItem =>
      Boolean(objects.find(object => object.text === schemaItem.sourceName)),
    );
  }, [dataSchema, objects]);

  const onApply = useCallback(async () => {
    if (location.payload.panel === RoutePanelName.Layers || location.payload.panel === RoutePanelName.Table) {
      addLayersToCurrentProject(createdLayersData);
    }

    if (location.payload.tab === RoutePanelName.Projects) {
      await createNewProject({
        input: {
          alias: meta.alias,
          description: meta.description,
          preview: meta.preview.resourcePreview,
          content: {
            ...defaultProjectContent,
            items: createLayerItems(createdLayersData, defaultScaleRange[0], defaultScaleRange[1]),
          },
          name: getResourceName(),
        },
      });
    }

    closeDialog();
  }, [
    addLayersToCurrentProject,
    closeDialog,
    createNewProject,
    defaultProjectContent,
    defaultScaleRange,
    location.payload.panel,
    location.payload.tab,
    meta,
  ]);

  const onCancel = useCallback(async () => {
    if (taskResult) {
      await cancel({ input: taskResult.taskId });
    }

    closeDialog();
  }, [cancel, closeDialog, taskResult]);

  useEffect(() => {
    if (!creatingLayersWithTables && createdLayersData) {
      runCopyTask({
        input: {
          continueOnFail: true,
          innerTasks: processedLayers.map((schemaItem, index) => {
            return getImportTaskParams(
              file.id,
              fileType,
              schemaItem.sourceName,
              createdLayersData[index]?.name,
              undefined,
              undefined,
              SchedulerTask.Copy,
            );
          }),
        },
      });

      setInProgress(true);
    }
  }, [createdLayersData, creatingLayersWithTables, processedLayers, file.id, fileType]);

  useEffect(() => {
    if (isTaskCompleted) {
      setInProgress(false);
    }
  }, [isTaskCompleted]);

  useEffect(() => {
    if (result?.stepResults) {
      const { errorCount, message } = result;
      const log = collectErrorLog(result.stepResults);

      message && setError(message);
      errorCount && setErrorsCount(errorCount);
      log && setErrorLog(log);
    }
  }, [result]);

  useEffect(() => {
    createLayers({
      input: processedLayers.map(({ sourceName, schemaAttributes, idAttribute, geometryAttribute, geometryType }) => {
        const attributes = Object.entries(schemaAttributes).map(([key, value]) => ({
          name: key,
          attributeName: key,
          srid: srFields.srid,
          ...value,
        }));

        attributes.unshift(createGidColumn(attributes, srFields.srid));

        return createLayerInput(
          {
            idField: getIdAttribute(attributes as ImportAttribute[], idAttribute),
            alias: sourceName,
            geometryAttribute: {
              attributeName: geometryAttribute,
            },
            geometryType,
            attributes: attributes as unknown as LayerInputConfiguration["attributes"],
            srid: srFields.srid,
          },
          true,
        );
      }),
    });
  }, []); // eslint-disable-line

  return {
    layersCreating: creatingLayersWithTables,
    copyingLayers: importInProgress,
    processed,
    isImportDone: isTaskCompleted,
    error: {
      message: error,
      count: errorsCount,
      log: errorLog,
    },
    onApply,
    onCancel,
  };
};
